import clsx from "clsx";
import { useState, useEffect, memo } from "react";
import { useMutation } from "@tanstack/react-query";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { Slot, FakeDash } from "@/components/ui/otpSlot";
import { validators } from "@/lib/security";
import { fetchOTP, verifyOTP } from "@/lib/utils";

const digitsOnly = (value: string) => (value || "").replace(/\D/g, "");

const ENTER_PHONE = "enterPhone";
const ENTER_OTP = "enterOtp";
const VERIFIED = "verified";

export interface PhoneValidationData {
	phone: string;
	phoneVerified: boolean;
	phoneStage: typeof ENTER_PHONE | typeof ENTER_OTP | typeof VERIFIED;
}

export interface PhoneValidationErrors {
	phone: boolean;
	phoneVerified: boolean;
}

interface FormDataProps {
	formData: PhoneValidationData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: PhoneValidationErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
}

function PhoneValidation({ formData, setFormData, formErrors, setFormErrors }: FormDataProps) {
	// OTP Data
	const [code, setCode] = useState("");
	const [txId, setTxId] = useState("");
	const [errorMsg, setErrMsg] = useState("");

	// Error Messages Triggered by Parent Component
	useEffect(() => {
		if (formErrors.phone || formErrors.phoneVerified) {
			if (
				formData.phoneStage === ENTER_PHONE &&
				!formData.phoneVerified &&
				formData.phone.length === 10 &&
				validators.phone(formData.phone)
			) {
				setErrMsg("Please verify your phone number.");
			} else if (
				(formData.phoneStage === ENTER_PHONE &&
					!formData.phoneVerified &&
					formData.phone.length <= 10) ||
				!validators.phone(formData.phone)
			) {
				setErrMsg("Please enter a valid 10-digit AU mobile number.");
			} else if (formData.phoneStage === ENTER_OTP && !formData.phoneVerified) {
				setErrMsg("Please enter a valid 4-digit OTP code.");
			}
			setFormErrors((prevErrors: any) => ({
				...prevErrors,
				phone: errorMsg ? true : false,
			}));
		}
	}, [formData.phoneStage, formData.phoneVerified, formData.phone, formErrors]);

	const sendOTP = useMutation({
		mutationKey: ["send-otp", formData.phone],
		mutationFn: async () => {
			const digits = digitsOnly(formData.phone);
			if (digits.length !== 10) {
				throw new Error("Please enter a valid 10-digit AU mobile number.");
			}
			const res = await fetchOTP(digits);
			return res;
		},
		onSuccess: async (data) => {
			setTxId(data.id);
			setFormData((prevData: any) => ({ ...prevData, phoneStage: ENTER_OTP }));
			setErrMsg("");
		},
		onError: async (err: any) => {
			setErrMsg(err.message);
		},
	});

	const confirmOTP = useMutation({
		mutationKey: [txId, code],
		mutationFn: async () => {
			if (!txId || !code) {
				throw new Error("OTP code is required for verification.");
			}
			const res = await verifyOTP(txId, code);
			return res;
		},
		onSuccess(data) {
			if (data.status == 200) {
				setFormData((prevData: any) => ({
					...prevData,
					phoneVerified: true,
					phoneStage: VERIFIED,
				}));
				setErrMsg("");
				setCode("");
			}
		},
		onError: (err: any) => {
			setErrMsg(err.message);
		},
	});

	const isSending = sendOTP.isPending;
	const isVerifying = confirmOTP.isPending;
	const isBusy = isSending || isVerifying;

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let phoneInput = e.target.value.replace(/[^0-9]/g, "");
		setFormData((prevData: any) => ({ ...prevData, phone: phoneInput }));

		if (formData.phoneStage === ENTER_PHONE && phoneInput.length < 10) return;

		// If user edits phone while verifying or entering OTP, reset flow
		if (formData.phoneStage !== ENTER_PHONE) {
			setFormData((prevData: any) => ({ ...prevData, phoneStage: ENTER_PHONE }));
			formData.phoneVerified
				? setFormData((prevData: any) => ({ ...prevData, phoneVerified: false }))
				: null;
			setTxId("");
			setCode("");
			setErrMsg("");
			setFormErrors((prevErrors: any) => ({
				...prevErrors,
				phone: false,
				phoneVerified: false,
			}));
		}
	};

	const handlePrimaryAction = () => {
		if (formData.phoneStage === ENTER_PHONE && formData.phone.length != 10) {
			setErrMsg("Please enter a valid 10-digit AU mobile number.");
			return;
		}

		if (
			formData.phoneStage === ENTER_PHONE &&
			formData.phone.length === 10 &&
			!validators.phone(formData.phone)
		) {
			setErrMsg("Please enter a valid 10-digit AU mobile number.");
			return;
		}

		if (digitsOnly(code).length < 4 && formData.phoneStage === ENTER_OTP) {
			setErrMsg("Please enter a valid 4-digit OTP code.");
			return;
		}
		setErrMsg("");

		if (formData.phoneStage === ENTER_PHONE) {
			(sendOTP as any).mutate();
		} else if (formData.phoneStage === ENTER_OTP) {
			(confirmOTP as any).mutate();
		}
	};

	return (
		<div className="jF-flex jF-flex-col jF-space-y-2">
			<label htmlFor="phone" className="jF-label">
				Phone Number
			</label>
			<div
				className={`jF-flex jF-flex-col sm:jF-flex-row jF-w-full jF-gap-2 ${
					formData.phoneStage === VERIFIED ? "jF-flex-col" : "jF-flex-row"
				}`}
			>
				{/* Phone input (hidden during OTP entry) */}
				{formData.phoneStage !== ENTER_OTP && (
					<div className="jF-relative jF-w-full jF-flex-grow">
						<input
							id="phone"
							name="phone"
							value={formData.phone}
							onChange={handlePhoneChange}
							type="tel"
							aria-describedby="phone-number"
							aria-label="Phone Number"
							inputMode="numeric"
							placeholder="04XX-XXX-XXX"
							maxLength={10}
							autoComplete="true"
							disabled={formData.phoneStage === VERIFIED}
							className={clsx(
								errorMsg && formErrors.phone ? "jF-input-error" : "jF-input-focus",
								"jF-input-default jF-pl-2.5",
								formData.phoneVerified &&
									"jF-pr-10 jF-ring-[--jF-theme-color] jF-ring-2 ",
							)}
						/>
						{formData.phoneVerified && (
							<div
								className="jF-absolute jF-inset-y-0 jF-right-3 jF-flex jF-items-center"
								aria-label="Phone verified"
							>
								<CheckCircleIcon
									className="jF-h-5 jF-w-5 jF-text-[--jF-theme-color]"
									aria-hidden="true"
								/>
							</div>
						)}
					</div>
				)}

				{/* OTP input slots (visible only during OTP entry) */}
				{formData.phoneStage === ENTER_OTP && (
					<div className="jF-flex jF-w-full jF-justify-center sm:jF-justify-start">
						<OTPInput
							value={code}
							onChange={setCode}
							maxLength={4}
							inputMode="numeric"
							pattern={REGEXP_ONLY_DIGITS}
							containerClassName="jF-flex jF-items-center jF-gap-2"
							render={({ slots }) => (
								<>
									{slots.map((slot, idx) => (
										<span
											key={idx}
											className="jF-flex jF-items-center jF-gap-2"
										>
											<Slot {...slot} />
											{idx === 1 && <FakeDash />}
										</span>
									))}
								</>
							)}
						/>
					</div>
				)}

				{!formData.phoneVerified && (
					<div className="jF-flex jF-w-full sm:jF-flex-none sm:jF-w-fit sm:jF-justify-end">
						<button
							type="button"
							onClick={handlePrimaryAction}
							disabled={isBusy}
							className={clsx(
								"jF-bg-[--jF-theme-color] jF-text-white jF-w-full sm:jF-min-w-[10rem] md:jF-min-w-0 lg:jF-min-w-[13rem] jF-text-sm sm:jF-text-base jF-font-medium jF-py-2 jF-px-2 lg:jF-px-4 jF-rounded-md hover:jF-bg-[--jF-hover-color]",
								isBusy && "jF-opacity-70 jF-cursor-not-allowed",
								formData.phoneStage === VERIFIED &&
									"jF-hidden jF-cursor-not-allowed",
							)}
						>
							{formData.phoneStage === ENTER_OTP
								? isVerifying
									? "Verifying..."
									: "Verify Code"
								: isSending
									? "Sending..."
									: "Send Verification Code"}
						</button>
					</div>
				)}
			</div>

			{errorMsg && <p className="jF-error-text">{errorMsg}</p>}
		</div>
	);
}

export default memo(PhoneValidation);
